type MetaTags = Array<React.JSX.IntrinsicElements["meta"]>

/**
 * Base configuration for all meta tags
 */
interface BaseMetaConfig {
  /**
   * The page title
   */
  title: string
  /**
   * The page description
   */
  description: string
  /**
   * The canonical URL for the page
   * @example "https://example.com/page"
   */
  url: string
  /**
   * The website name
   */
  siteName?: string
  /**
   * The locale of the page
   * @default "en_US"
   */
  locale?: string
  /**
   * Type of resource
   * @default "website"
   */
  type?: string
}

/**
 * Configuration for Open Graph meta tags
 */
interface OpenGraphConfig extends BaseMetaConfig {
  /**
   * The URL to the image to use for Open Graph
   */
  image?: string
  /**
   * Alternative text for the image
   */
  imageAlt?: string
  /**
   * Width of the image
   */
  imageWidth?: number
  /**
   * Height of the image
   */
  imageHeight?: number
  /**
   * When the content was published
   */
  publishedTime?: string
  /**
   * When the content was last modified
   */
  modifiedTime?: string
  /**
   * Article author
   */
  author?: string
  /**
   * Article section
   */
  section?: string
  /**
   * Article tags
   */
  tags?: string[]
}

/**
 * Configuration for Twitter Card meta tags
 */
interface TwitterConfig {
  /**
   * The type of Twitter card
   * @default "summary_large_image"
   */
  card?: "summary" | "summary_large_image" | "app" | "player"
  /**
   * Twitter @username of website
   */
  site?: string
  /**
   * Twitter @username of content creator
   */
  creator?: string
  /**
   * Whether to hide Twitter share info
   */
  dnt?: boolean
}

/**
 * Complete meta tag configuration
 */
interface MetaTagsConfig {
  /**
   * Base configuration for standard meta tags
   */
  base: BaseMetaConfig
  /**
   * Open Graph configuration
   */
  openGraph?: OpenGraphConfig
  /**
   * Twitter card configuration
   */
  twitter?: TwitterConfig
  /**
   * Robots directives
   * @example ["index", "follow"]
   */
  robots?: string[]
}

/**
 * Generates meta tags for SEO and social media sharing
 * Compatible with React Router v7's MetaFunction return type
 */
export const generateMetaTags = (config: MetaTagsConfig): MetaTags => {
  const { base, openGraph, twitter, robots = ["index", "follow"] } = config

  // Start with the title
  const metaTags: MetaTags = [
    {
      charSet: "utf-8",
    },
    {
      name: "viewport",
      content:
        "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no",
    },
    { title: base.title },
    { name: "description", content: base.description },
    { name: "robots", content: robots.join(", ") },
  ]

  // Open Graph tags
  const og = openGraph || base
  metaTags.push(
    { property: "og:title", content: og.title },
    { property: "og:description", content: og.description },
    { property: "og:url", content: og.url },
    { property: "og:type", content: og.type || "website" },
  )

  if (og.siteName) {
    metaTags.push({
      property: "og:site_name",
      content: og.siteName,
    })
  }

  if (og.locale) {
    metaTags.push({
      property: "og:locale",
      content: og.locale,
    })
  }

  // Image related tags - only available in OpenGraphConfig
  if (openGraph?.image) {
    metaTags.push({
      property: "og:image",
      content: openGraph.image,
    })

    if (openGraph.imageAlt) {
      metaTags.push({
        property: "og:image:alt",
        content: openGraph.imageAlt,
      })
    }

    if (openGraph.imageWidth) {
      metaTags.push({
        property: "og:image:width",
        content: String(openGraph.imageWidth),
      })
    }

    if (openGraph.imageHeight) {
      metaTags.push({
        property: "og:image:height",
        content: String(openGraph.imageHeight),
      })
    }
  }

  // Article specific Open Graph tags
  if (og.type === "article" && openGraph) {
    if (openGraph.publishedTime) {
      metaTags.push({
        property: "article:published_time",
        content: openGraph.publishedTime,
      })
    }

    if (openGraph.modifiedTime) {
      metaTags.push({
        property: "article:modified_time",
        content: openGraph.modifiedTime,
      })
    }

    if (openGraph.author) {
      metaTags.push({
        property: "article:author",
        content: openGraph.author,
      })
    }

    if (openGraph.section) {
      metaTags.push({
        property: "article:section",
        content: openGraph.section,
      })
    }

    if (openGraph.tags && openGraph.tags.length > 0) {
      openGraph.tags.forEach((tag) => {
        metaTags.push({
          property: "article:tag",
          content: tag,
        })
      })
    }
  }

  // Twitter Card tags
  if (twitter) {
    metaTags.push({
      name: "twitter:card",
      content: twitter.card || "summary_large_image",
    })

    if (twitter.site) {
      metaTags.push({
        name: "twitter:site",
        content: twitter.site,
      })
    }

    if (twitter.creator) {
      metaTags.push({
        name: "twitter:creator",
        content: twitter.creator,
      })
    }

    if (twitter.dnt) {
      metaTags.push({
        name: "twitter:dnt",
        content: "on",
      })
    }
  }

  // If twitter tags aren't provided, add default ones using open graph data
  if (!twitter) {
    metaTags.push({
      name: "twitter:title",
      content: og.title,
    })

    metaTags.push({
      name: "twitter:description",
      content: og.description,
    })

    if (openGraph?.image) {
      metaTags.push({
        name: "twitter:image",
        content: openGraph.image,
      })

      if (openGraph.imageAlt) {
        metaTags.push({
          name: "twitter:image:alt",
          content: openGraph.imageAlt,
        })
      }
    }
  }

  return metaTags
}
